import Close from "@mui/icons-material/Close";
import { Backdrop, Button, Fade, IconButton, Modal } from "@mui/material";
import { makeStyles } from "@mui/styles";
import React, {
    Ref,
    forwardRef,
    useContext,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from "react";

import { UserAppContext } from "contexts";
import get from "lodash/get";
import {
    useReactiveUserSubscriptionV2Mutation,
    useRetriveStripeCustomerQuery,
    useSharedQuery,
    useTrackingSubscriptionAllActionMutation
} from "services/webServer";
import MembershipOfferClaimedModal, {
    IMembershipOfferClaimedModalRef,
} from "../MembershipOfferClaimedModal/MembershipOfferClaimedModal";
import MembershipOfferInvalidPaymentModal, {
    IMembershipOfferInvalidPaymentModalRef,
} from "../MembershipOfferInvalidPaymentModal/MembershipOfferInvalidPaymentModal";

import { useHideExternalIcon } from "Hooks/useHideExternalIcon";
import { useGetSubscriptionData } from 'Screens/Memberships/Detail/ReviewAndCancel/ReviewAndCancel.hooks';
import Swal from "sweetalert2";

import { useHistory } from "react-router-dom";
import { MEMBERSHIP_OFFER_KEY } from "../MembershipsMain.constant";
import styles from "./MembershipOfferModal.module.scss";

const useStyles = makeStyles({
    modal: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",

        "@media (max-width: 768px)": {
            alignItems: "flex-start",
            marginTop: "20px",
        },
    },
});

interface IMembershipOfferModalProps {
    membershipId: string;
    planId: string
}

export interface IMembershipOfferModalRef {
    setIsVisible: (visible: boolean) => void;
    isVisible: boolean;
}
const dataMoreDetail = [
    {
        id: 1,
        text: "Once you click the button below, your new discounted membership will be activated.",
    },
    {
        id: 2,
        text: "You’ll be charged immediately and gain full access right away. In addition, you'll automatically receive a free 20-year warranty for your products, valid for as long as your membership remains active.",
    },
    {
        id: 3,
        text: "You can cancel your membership at any time after activation.",
    }
];

const MembershipOfferModal = (
    {
        membershipId = "",
        planId
    }: IMembershipOfferModalProps,
    ref: Ref<IMembershipOfferModalRef>
) => {
    const [isVisible, setIsVisible] = useState<boolean>(false);
    const [trackingSubscriptionAllAction] = useTrackingSubscriptionAllActionMutation()

    useHideExternalIcon(isVisible);

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const classes = useStyles();
    const { auth } = useContext(UserAppContext);
    const { data } = useSharedQuery();
    const history = useHistory();

    const { subscriptionData, isLoadingSubscription } = useGetSubscriptionData(membershipId)

    const [reactiveUserSubscriptionV2] =
        useReactiveUserSubscriptionV2Mutation();

    const membershipInvalidPaymentRef =
        useRef<IMembershipOfferInvalidPaymentModalRef>(null);

    const membershipClaimedRef = useRef<IMembershipOfferClaimedModalRef>(null);

    const stripeCustomerId = get(
        data,
        "currentUser.profile.stripe.customer_id"
    );

    const { data: customerData } = useRetriveStripeCustomerQuery({
        variables: {
            customerId: stripeCustomerId,
            params: {
                "expand[]": "invoice_settings.default_payment_method",
            },
        },
        fetchPolicy: "no-cache",
        skip: Boolean(!stripeCustomerId),
    });


    const cardInfo = useMemo(() => {
        if (!customerData || !customerData?.retriveStripeCustomer)
            return undefined;

        const defaultPaymentInfo =
            customerData?.retriveStripeCustomer?.invoice_settings
                ?.default_payment_method || undefined;

        if (defaultPaymentInfo) {
            return {
                exp_month: defaultPaymentInfo?.card?.exp_month,
                exp_year: defaultPaymentInfo?.card?.exp_year,
            };
        }
        return undefined;
    }, [customerData]);

    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const handleClose = () => {
        setIsVisible(false);
    };

    const handleCheckCardExpired = ({
        expMonth,
        expYear,
    }: {
        expMonth: number;
        expYear: number;
    }) => {
        if (!expMonth || !expYear) return true;

        const currentDate = new Date();
        const currentMonth = currentDate.getMonth() + 1;
        const currentYear = currentDate.getFullYear();

        return (
            expYear < currentYear ||
            (expYear === currentYear && expMonth < currentMonth)
        );
    };

    const currency = useMemo(() => {
        return subscriptionData?.avironSubscription?.plan?.currency?.toUpperCase() || "USD"
    }, [subscriptionData])

    const handleClickRedeemOffer = async () => {
        const isExpired = handleCheckCardExpired({
            expMonth: cardInfo?.exp_month,
            expYear: cardInfo?.exp_year,
        });
        if (isExpired) {
            setIsVisible(false);
            trackingSubscriptionAllAction({
                variables: {
                    id: membershipId,
                    action: "reactive",
                    payload: {
                        offer: { name: "50% off for a year" },
                        source: "cms",
                        plan_id: planId,
                        customer_id: data?.currentUser?.id || "",
                        customer_type: "user",
                        status: "failed",
                        message: "Your payment info is invalid",
                    },
                },
            });
            membershipInvalidPaymentRef?.current?.setIsVisible(true);
            return;
        }

        if (!auth?.email || Boolean(isSubmitting)) return;
        try {
            setIsSubmitting(true);
            await reactiveUserSubscriptionV2({
                variables: {
                    id: membershipId,
                },
            });

            setIsVisible(false);
            setIsSubmitting(false);

            membershipClaimedRef?.current?.setIsVisible(true);
        } catch (error) {
            setIsVisible(false);

            const isCardDeclined =
                error.graphQLErrors?.[0]?.key === "stripe_card_declined";
            const isCardExpired = error.graphQLErrors?.[0]?.message?.includes(
                "Your card has expired"
            );
            if (isCardDeclined) {
                await Swal.fire(
                    "Oops",
                    `
                Your card was declined. Please update your payment methods and try again
                `,
                    "error"
                );
                history.push(`/payment_method`);
            } else if (isCardExpired) {

                await Swal.fire(
                    "Oops",
                    `
                Your card has expired. Please update your payment methods and try again
                `,
                    "error"
                );
                history.push(`/payment_method`);
            } else {
                await Swal.fire(
                    "Oops",
                    `
                It seem like you’re not eligible for
                this offer.
                `,
                    "error"
                );
                localStorage.removeItem(MEMBERSHIP_OFFER_KEY);
                setIsSubmitting(false);
                window.location.reload();
            }
        }
    };

    useImperativeHandle(
        ref,
        () => ({
            isVisible,
            setIsVisible,
        }),
        [isVisible]
    );

    useEffect(() => {
        setIsLoading(isLoadingSubscription)
    }, [isLoadingSubscription])

    if (isLoading) return <></>;

    return (
        <>
            <Modal
                open={isVisible}
                onClose={handleClose}
                className={classes.modal}
                aria-labelledby="transition-modal-title"
                aria-describedby="transition-modal-description"
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 500,
                }}
            >
                <Fade in={isVisible}>
                    <div className={styles["membership_offer_modal"]}>
                        <div
                            className={styles["membership_offer_modal-header"]}
                        >
                            <IconButton
                                onClick={handleClose}
                                className="close-button"
                            >
                                <Close />
                            </IconButton>
                        </div>

                        <div
                            className={styles["membership_offer_modal-content"]}
                        >
                            <div className={styles["header"]}>
                                <div>
                                    <img
                                        src="/images/aviron-logo.png"
                                        alt="Aviron Logo"
                                        width={129}
                                        height={25}
                                        style={{
                                            objectFit: "contain"
                                        }}
                                    />
                                    <h2
                                        className={
                                            styles["membership_offer-title"]
                                        }
                                    >
                                        WELCOME BACK
                                        {data?.currentUser?.firstName &&
                                            ","}{" "}
                                        <span className={styles["highlight"]}>
                                            {data?.currentUser?.firstName || ""}
                                        </span>
                                    </h2>
                                    <p
                                        className={
                                            styles[
                                            "membership_offer-description"
                                            ]
                                        }
                                    >
                                        50% OFF <br />
                                        FOR A YEAR
                                    </p>
                                </div>
                                <img
                                    src="/images/membership/membership-offer.webp"
                                    alt="Aviron Membership Offer"
                                    className={styles["membership_offer-image"]}
                                />
                            </div>
                            <div className={styles["content"]}>
                                <div
                                    className={styles["membership_offer-data"]}
                                >
                                    <div>
                                        <p className={styles["data-tag"]}>
                                            OFFER EXPIRES SOON
                                        </p>
                                        <p className={styles["data-title"]}>
                                            ANNUAL FAMILY MEMBERSHIP WITH 20-YEAR WARRANTY
                                            {/* {lastestSubscription?.name} */}
                                        </p>
                                        <p
                                            className={
                                                styles["data-description"]
                                            }
                                        >
                                            {currency === "USD" ? "$204/year" : "$264/year"}, billed annually
                                        </p>
                                    </div>
                                    <div>
                                        <p className={styles["price_original"]}>
                                            {currency === "USD" ? "$34" : "$44"}
                                        </p>

                                        <p className={styles["price_discount"]}>
                                            {currency === "USD" ? "$17" : "$22"}/
                                            <span className={styles["unit"]}>
                                                MONTH
                                            </span>
                                        </p>
                                    </div>
                                    <div className={styles["currency"]}>
                                        {currency}
                                        <img
                                            src="/images/icon-down.png"
                                            alt="Aviron Membership Offer"
                                            width={8}
                                            height={4}
                                            style={{
                                                marginLeft: "8px",
                                                marginBottom: "3px",
                                            }}
                                        />
                                    </div>
                                </div>
                                <ul
                                    className={
                                        styles["membership_offer_more-info"]
                                    }
                                >
                                    {dataMoreDetail.map((item) => (
                                        <li
                                            className={
                                                styles[
                                                "membership_offer_more-info-item"
                                                ]
                                            }
                                            key={item.id}
                                        >
                                            {item.text}
                                        </li>
                                    ))}
                                </ul>
                                <div
                                    className={
                                        styles["membership_offer-button"]
                                    }
                                >
                                    <Button
                                        className={styles["btn-claim-offer"]}
                                        variant="contained"
                                        onClick={handleClickRedeemOffer}
                                        color="secondary"
                                        sx={{
                                            marginTop: "24px",
                                            marginBottom: "8px",
                                            fontSize: "16px",
                                        }}
                                    >
                                        {isSubmitting
                                            ? "REDEEMING..."
                                            : "REDEEM OFFER"}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </Fade>
            </Modal>
            <MembershipOfferInvalidPaymentModal
                ref={membershipInvalidPaymentRef}
            />
            <MembershipOfferClaimedModal
                ref={membershipClaimedRef}
                id={membershipId}
            />
        </>
    );
};

export default forwardRef(MembershipOfferModal);
